import type {
  CustomFieldType,
  CustomFieldVisibilityType,
  RelatedObjectType,
} from './custom-fields.constants';
import type { HiddenFieldsByModule } from 'src/app/core/store/field-visibility/field-visibility.types';
import { PriceCandidateSource } from 'src/app/features/quotations/models/quotations.models';

export interface IUserModule {
    module: string;
    own_data: boolean;
}

export interface IUser {
    id: number;
    name: string;
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    image: string;
    enabled: boolean;
    is_deleted?: boolean;
    deleted_at?: string | null;
    roles: IRole[];
    role_ids: number[];
    lang?: string;
    last_activity?: string;
    receive_emails?: boolean;
    is_super_admin?: boolean;
    modules?: IUserModule[];
}

export interface IUserCreate {
    name: string;
    email: string;
    enabled: boolean;
    role_ids: number[];
}

export interface IUserUpdate extends Partial<IUserCreate> {
    id: number;
}

export interface IUserFormPayload {
    name: string;
    email: string;
    roleId: number;
}

export interface IRole {
    id: number;
    name: string;
    description: string;
    permissions: IPermission[];
    permission_ids: number[];
    user_ids: number[];
    users: IUser[];
    edited_by?: {
        id: number;
        name: string;
    };
    last_edited?: string;
    is_protected?: boolean;
}

export interface IAddRole {
    name: string;
}

export interface IUpdateRole {
    name?: string;
    permission_ids?: number[];
    hidden_field_ids?: HiddenFieldsByModule;
}

/**
 * A member as the `/v1/teams` endpoints return it — a strict subset of `IUser`.
 * The backend serialises only these three fields, so typing it as a full `IUser`
 * would let callers read `email` or `roles` and get `undefined` with no error.
 */
export type ITeamMember = Pick<IUser, 'id' | 'name' | 'image'>;

export interface ITeam {
    id: number;
    name: string;
    description?: string;
    member_ids: number[];
    members: ITeamMember[];
    member_count: number;
    /** Pre-formatted `"YYYY-MM-DD HH:mm:ss"`, NOT an ISO datetime. Parse explicitly before reformatting. */
    last_edited: string;
}

/**
 * A team as `GET /v1/teams/options` returns it: id and name, nothing else.
 *
 * Deliberately NOT `ITeam`. `/v1/teams` is gated behind `access:settings_team` and carries
 * members and counts; the options route is readable by any order/inbox user and carries
 * neither. Sharing one type would invite a picker to read `members` and get `undefined`.
 */
export type ITeamOption = Pick<ITeam, 'id' | 'name'>;

export interface IAddTeam {
    name: string;
    description?: string;
    member_ids?: number[];
}

export interface IUpdateTeam {
    name?: string;
    description?: string;
    /** Sent verbatim when present — `[]` clears every member, it is not "no change". */
    member_ids?: number[];
}

/** The only three columns `GET /v1/teams` accepts. `write_date` is rejected with a 400. */
export const TEAM_SORT_FIELDS = ['name', 'member_count', 'last_edited'] as const;
export type TeamSortField = typeof TEAM_SORT_FIELDS[number];
/** The API is case-sensitive here: `ASC` returns 400. */
export type TeamSortMode = 'asc' | 'desc';

export interface ITeamSort {
    sort_by: TeamSortField;
    sort_mode: TeamSortMode;
}

export type RoleFieldVisibilityLevel = 'visible' | 'hidden';

export interface IPermission {
    id: number;
    name: string;
    description: string;
    module_id?: number | null;
}

export interface IModule {
    id: number;
    name: string;
    permissions: IPermission[]
}

export interface INotificationType {
    id: number;
    name: string;
}

export interface INotification {
    id: number;
    users: IUser[];
    types: INotificationType[];
}

export interface INotificationTypeUpdate {
    type_ids: number[];
    user_ids: number[];
}

export interface ICustomField {
    id: number;
    technical_name: string;
    object_type: RelatedObjectType;
    field_type: CustomFieldType;
    label: { [key: string]: string };
    help_text: { [key: string]: string };
    allowed_values: string[];
    visibility: CustomFieldVisibilityType[];
    is_required: boolean;
    erp_mapping_key: string | null;
}

export interface IApplicabilityField {
    id: number;
    technical_name: string;
    label: string;
    field_type: string;
    value_options: string[];
}

// interface for the custom field form
export interface ICustomFieldForm {
    object_type: RelatedObjectType;
    field_type: CustomFieldType;
    label: { [key: string]: string };
    help_text: { [key: string]: string };
    allowed_values: string[];
    visibility: CustomFieldVisibilityType[];
    is_required: boolean;
    erp_mapping_key: string | null;
}


export interface IObjectCustomFields {
    values: {
      [key: string]: string | string[] | number | boolean;
    };
    definitions: ICustomField[];
  }

export interface IObjectCustomFieldsParams {
    object_type: RelatedObjectType;
    record_id: number;
    visibility?: CustomFieldVisibilityType[];
    include_definitions?: boolean;
}

// Smart Inbox settings (mailboxes)
export type MailboxProvider = 'gmail' | 'o365' | 'exchange';
export type MailboxType = 'personal' | 'team';
export type ConnectionStatus = 'connected' | 'disconnected';
export type SyncStatus = 'idle' | 'syncing' | 'error' | 'paused';

export interface IMailboxSettings {
  id: number;
  name: string;
  email: string;
  provider: MailboxProvider;
  mailbox_type: MailboxType;
  mailbox_owner_id: number | null;
  mailbox_owner_name: string | null;
  can_view_ids: number[];
  can_send_ids: number[];
  connection_status: ConnectionStatus;
  sync_status: SyncStatus;
  last_sync_at: string | null;
  last_sync_error: string | null;
  active: boolean;
  unread_count?: number;
  users_with_access?: { user_id: number; name: string }[];
}

export interface IMailboxSettingsUpdate {
  name?: string;
  can_view?: number[];
  can_send?: number[];
}

/**
 * Company-level default price source for orders.
 * - `volta`     — use System (Volta) prices (default).
 * - `extracted` — use AI-extracted prices from customer documents.
 * - `none`      — create orders without pre-filled prices.
 */
/**
 * The three historical values are presets; `custom` is the fourth, and it is
 * what the ordered list writes.
 *
 * `custom` is added, never substituted: a tenant that never opens the screen
 * keeps its card, its behaviour and an untouched database.
 */
export type OrderPriceSource = 'volta' | 'extracted' | 'none' | 'custom';

/**
 * The sources a tenant may order.
 *
 * Derived, not re-spelled: this screen orders exactly the sources the pricing
 * engine can produce a candidate from, minus `manual` — a keystroke is the
 * answer, not a source. Written out as its own union, a source added to the
 * engine would reach the candidate list and never the settings that order it.
 */
export type PriceSourceKind = Exclude<PriceCandidateSource, 'manual'>;

export const PRICE_SOURCE_KINDS: PriceSourceKind[] = [
  'extracted',
  'observation',
  'history',
  'catalog',
];

export interface IPricePolicySettings {
  price_source_preset: OrderPriceSource;
  price_sources: PriceSourceKind[];
  /** Optional per-source window, in days. Absent means "no window", never zero. */
  price_freshness_days: Partial<Record<PriceSourceKind, number>>;
  /** False when the tenant never opened this screen — the legacy setting still rules. */
  configured: boolean;
  legacy_company_source?: string;
  legacy_agent_source?: string;
}

/**
 * What the Orders settings screen sends.
 *
 * Flat, and deliberately not `IOrdersSettings`: the route reads the policy
 * fields at the top level of the body, and nesting them here would make the
 * shape sent and the shape read differ by one level — a mismatch that fails
 * silently, since a PATCH ignores what it does not recognise. Every field is
 * optional: a screen that always sent everything would overwrite a setting it
 * never showed.
 */
export interface IOrdersSettingsUpdate {
  default_price_source?: OrderPriceSource;
  hide_pricing_columns?: boolean;
  /** null clears the policy: back to the inherited state. */
  price_source_preset?: OrderPriceSource | null;
  price_sources?: PriceSourceKind[];
  price_freshness_days?: Partial<Record<PriceSourceKind, number>>;
}

export interface IOrdersSettings {
  default_price_source: OrderPriceSource;
  /** Absent when the pricing module is not installed — absent, not empty. */
  price_policy?: IPricePolicySettings;
  /**
   * Only applicable when `default_price_source === 'none'`. When true, pricing
   * and metrics columns are hidden BY DEFAULT on the orders list, order details
   * and side-by-side views (users can still re-enable them per-view).
   */
  hide_pricing_columns: boolean;
}

// Smart Replies settings (global AI reply settings — WB-6371 scope v3)
// Two presentation rules only. The sign-off block comes from the mailbox
// signature and stock handling is hardcoded by Alta, so neither is exposed here.
export type SmartRepliesPricingSource = 'any' | 'kb' | 'db' | 'placeholder';
export type SmartRepliesProductFormat = 'bullets' | 'table';

export interface ISmartRepliesSettings {
  pricing_source: SmartRepliesPricingSource;
  product_format: SmartRepliesProductFormat;
}

export interface ISplittingRule {
  id: number;
  name: string;
  active: boolean;
  sequence: number;
  condition_type: 'brand' | 'category' | 'product_label' | 'product_custom_field';
  condition_custom_field_id: number | null;
  condition_custom_field_name: string | null;
  split_mode: 'all' | 'specific';
  split_values: string[];
  applicability_type: 'all' | 'customer' | 'customer_group' | 'customer_custom_field';
  applicability_partner_ids: { id: number; name: string }[];
  applicability_company_ids: { id: number; name: string }[];
  applicability_location_group_ids: { id: number; name: string }[];
  applicability_custom_field_id: number | null;
  applicability_custom_field_name: string | null;
  applicability_custom_field_value: string | null;
  log_count: number;
}

export interface ISplittingRuleCreate {
  name: string;
  active: boolean;
  sequence?: number;
  condition_type: 'brand' | 'category' | 'product_label' | 'product_custom_field';
  condition_custom_field_id?: number | null;
  split_mode: 'all' | 'specific';
  split_values: string[];
  applicability_type: 'all' | 'customer' | 'customer_group' | 'customer_custom_field';
  applicability_partner_ids?: number[];
  applicability_company_ids?: number[];
  applicability_location_group_ids?: number[];
  applicability_custom_field_id?: number | null;
  applicability_custom_field_value?: string | null;
}
// ── Labels (`/v1/labels`) ────────────────────────────────────────────────────

/**
 * Where the definition came from. Drives the visual treatment and, more
 * importantly, what the edit form may offer — see `ILabel` below.
 *
 * `erp` is a third value beyond the epic's two, seeded by Phase 57. Typed here
 * so a component switching on origin has to acknowledge it rather than falling
 * through the `manual` branch.
 */
export type LabelOrigin = 'manual' | 'provider_sync' | 'erp';

/**
 * `DEFAULT_LABEL_COLOR` on the `wolsal.label` model, repeated here so an unedited
 * label looks identical whether a user made it in this UI or ERP seeded it.
 * Changing one without the other makes the two paths diverge silently.
 */
export const DEFAULT_LABEL_COLOR = '#6B7280';

/**
 * The colour choices the form offers. The API accepts any `^#[0-9A-Fa-f]{6}$`,
 * so this is a UI narrowing, not a contract: it keeps chips legible against the
 * list background and stops a user picking white on white. Free hex entry can be
 * added later without a contract change; a palette retro-fitted after users have
 * picked arbitrary colours cannot.
 */
export const LABEL_COLOR_PALETTE: readonly string[] = [
    DEFAULT_LABEL_COLOR,
    '#EF4444',
    '#F97316',
    '#F59E0B',
    '#10B981',
    '#06B6D4',
    '#3B82F6',
    '#6366F1',
    '#8B5CF6',
    '#EC4899',
];

/**
 * `GET /v1/labels` caps `limit` at `MAX_LIMIT` (500) and defaults it to 100. The
 * Labels screen is unpaginated, so it asks for the cap: leaving the default in
 * place would silently hide a tenant's 101st label behind no affordance at all.
 * Past 500 this screen needs real pagination, not a bigger number.
 */
export const LABEL_LIST_LIMIT = 500;

/**
 * A label as `/v1/labels` returns it.
 *
 * Three fields are NOT writable the way a reader might assume, and the backend's
 * DTOs use `extra='ignore'` — so a PATCH carrying one of them is a silent no-op
 * rather than an error, which is the worst way to find out:
 *
 * - `origin` is server-assigned. It is absent from both request DTOs and forced
 *   to `manual` on create; ERP and provider labels get theirs from the code path
 *   that creates them.
 * - `name` and `description` are refused with **400** on a `provider_sync`
 *   label. `color` on the same record is **accepted** — the asymmetry is
 *   deliberate and mutation-proved, so do not lock the whole record.
 * - `visible` and `eligible` are writable through PATCH only, never at create.
 *   The pair is constrained: eligible-but-hidden is a 400 from the model, so the
 *   form makes that combination unreachable instead of surfacing the error.
 */
/** Tokens, not sentences: the backend names the link, the UI names it for a reader. */
export type LabelSeededFrom = 'product_category' | 'location_group';

/**
 * A label as `GET /v1/labels/options` returns it: what the conversation picker and
 * the inbox filter need, readable by any inbox user. Hidden labels are included.
 */
export interface ILabelOption {
    id: number;
    name: string;
    color: string;
    visible: boolean;
}

export interface ILabel {
    id: number;
    name: string;
    description: string;
    /** Always a 6-digit `#RRGGBB`; the model requires it and defaults it. */
    color: string;
    origin: LabelOrigin;
    /**
     * Which ERP source a seeded label came from, or null for everything else.
     * `origin` says erp vs manual vs provider; this says WHICH erp source.
     * Derived server-side from the link column, so it cannot go stale the way a
     * snapshot written into `description` would.
     */
    seeded_from?: LabelSeededFrom | null;
    visible: boolean;
    eligible: boolean;
    /** ISO 8601, unlike `ITeam.last_edited` which is pre-formatted. */
    created_at?: string | null;
    updated_at?: string | null;
}

/**
 * Create payload. `visible` / `eligible` are deliberately absent: the create DTO
 * does not declare them and a new label always lands visible and eligible from
 * the model defaults. Governance is a separate toggle, on a screen that already
 * has the label in front of it.
 */
/**
 * A label a connected mailbox already carries, and what the tenant decided.
 *
 * NOT a label. While `state` is 'pending' there is no `wolsal.label` behind
 * this at all, which is exactly why a pending one cannot be filtered on or used
 * in an automation -- there is nothing for those paths to find. `labelId` is
 * null until it is imported, and that null is the whole state.
 */
/** GET/PUT /v1/labels/seeding. `created` is only present on a PUT that turned a source on. */
export interface ILabelSeeding {
    product_categories: boolean;
    company_groups: boolean;
    created?: { product_categories?: number; company_groups?: number };
}

export interface IProviderLabel {
    id: number;
    mailbox_id: number;
    mailbox_email: string;
    /** The provider's own id -- a Gmail `Label_xxx`, an Outlook category id. */
    external_id: string;
    name: string;
    state: 'pending' | 'accepted' | 'rejected';
    label_id: number | null;
}

/**
 * Create only. `visible` and `eligible` are deliberately absent: the create DTO does
 * not declare them and drops what it does not declare, so a new label always lands
 * visible and eligible. They are writable through PATCH, and only there.
 */
export interface IAddLabel {
    name: string;
    description?: string;
    color?: string;
}

/**
 * PATCH payload. Every field optional, but an entirely empty body is a 400 —
 * the backend requires at least one of these five keys.
 */
export interface IUpdateLabel {
    name?: string;
    description?: string;
    color?: string;
    visible?: boolean;
    eligible?: boolean;
}
